import React, { useState, useEffect } from 'react';
import { Download, Send, Plus } from 'lucide-react';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import toast from 'react-hot-toast';

import { API_BASE_URL, getUserImageUrl } from '../config/api';
function Payments() {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  const [paymentData, setPaymentData] = useState({
    userId: '',
    amount: '',
    type: 'Session',
    paymentDate: new Date().toISOString().split('T')[0]
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);
useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.relative')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const fetchPayments = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/payments?page=${page}&limit=10`);
      
      if (response.data.payments && response.data.pagination) {
        setPayments(response.data.payments);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.totalItems,
          itemsPerPage: response.data.pagination.itemsPerPage,
          hasNextPage: response.data.pagination.hasNextPage,
          hasPreviousPage: response.data.pagination.hasPreviousPage
        });
      } else {
        // Fallback for old API format
        setPayments(response.data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      // Filter for riders and owners only, sorted by role then name
      const ridersAndOwners = response.data.users
        .filter(user => user.role === 'Rider' || user.role === 'Owner')
        .sort((a, b) => {
          // Sort by role first (Riders first), then by name
          if (a.role !== b.role) {
            return a.role === 'Rider' ? -1 : 1;
          }
          return (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName);
        });
      setUsers(ridersAndOwners);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to mock data if API fails
      setUsers([
        { id: 1, firstName: 'Ahmed', lastName: 'Rider', email: 'ahmed@rider.com', role: 'Rider', imagePath: null },
        { id: 2, firstName: 'Fatima', lastName: 'Owner', email: 'fatima@owner.com', role: 'Owner', imagePath: null },
        { id: 3, firstName: 'Omar', lastName: 'Rider', email: 'omar@rider.com', role: 'Rider', imagePath: null },
        { id: 4, firstName: 'Aisha', lastName: 'Owner', email: 'aisha@owner.com', role: 'Owner', imagePath: null }
      ]);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected user info
  const selectedUser = users.find(u => u.id.toString() === paymentData.userId);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiEndpoints.payments.create({
        userId: parseInt(paymentData.userId),
        amount: parseFloat(paymentData.amount),
        type: parseInt(paymentData.type),
        description: `Payment - ${paymentData.amount} DA`,
        dueDate: new Date(paymentData.paymentDate).toISOString()
      });

      toast.success('Payment created successfully!');
      setShowPaymentForm(false);
      setPaymentData({
        userId: '',
        amount: '',
        type: 'Session',
        paymentDate: new Date().toISOString().split('T')[0]
      });
      fetchPayments(pagination.currentPage); // Refresh current page
    } catch (error) {
      toast.error('Error creating payment');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = (payment) => {
    const user = payment.user; // Use lowercase 'user' from API response
    if (!user) return;

    const invoiceContent = `
INVOICE
=======

Invoice #: INV-${payment.id.toString().padStart(6, '0')}
Date: ${new Date(payment.createdAt).toLocaleDateString()}
Payment Date: ${new Date(payment.createdAt).toLocaleDateString()}

BILL TO:
--------
Name: ${user.name || user.email}
Email: ${user.email}
Phone: N/A

PAYMENT DETAILS:
----------------
Description: ${payment.description || 'Payment'}
Amount: ${payment.amount} DA
Type: ${payment.type}
Status: ${payment.status}

Thank you for your payment!

Generated on: ${new Date().toLocaleString()}
    `;

    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${payment.id}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    toast.success('Invoice downloaded!');
  };

  const sendInvoice = async (payment) => {
    const user = payment.user; // Use lowercase 'user' from API response
    if (!user || !user.email) {
      toast.error('User email not found');
      return;
    }

    try {
      // Here you would integrate with an email service
      // For now, we'll just show a success message
      toast.success(`Invoice sent to ${user.email}`);
    } catch (error) {
      toast.error('Error sending invoice');
    }
  };

  return (
    <>
      <div className="flex flex-col h-full" dir="ltr" key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="flex-shrink-0">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payments</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">
                Manage payment records and financial transactions
              </p>
            </div>
            <button
              onClick={() => setShowPaymentForm(true)}
              className="bg-primary-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">New Payment</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search payments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full py-4 px-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Clear
                </button>
                
                <button
                  onClick={() => fetchPayments()}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User & Files
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => {
                const user = payment.user; // Use lowercase 'user' from API response
                return (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {/* User Avatar */}
                        <div className="flex-shrink-0">
                          {getUserImageUrl(user) ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={getUserImageUrl(user)}
                              alt={user?.name || 'User'}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center ${getUserImageUrl(user) ? 'hidden' : 'flex'}`}>
                            <span className="text-sm font-medium text-gray-700">
                              {user ? (user.name?.[0] || user.email?.[0] || 'U') : 'U'}
                            </span>
                          </div>
                        </div>
                        
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {user?.firstName && user?.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : user?.name || user?.email || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">{user?.email}</div>
                          <div className="text-xs text-gray-400">
                            Role: {user?.role || 'Unknown'}
                          </div>
                          
                          {/* User Files */}
                          {getUserImageUrl(user) && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                📁 Profile Image
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {payment.amount} DA
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        {payment.type}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.status === 'Completed' 
                          ? 'bg-green-100 text-green-800' 
                          : payment.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => generateInvoice(payment)}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50"
                        >
                          <Download className="w-4 h-4" />
                          Invoice
                        </button>
                        <button
                          onClick={() => sendInvoice(payment)}
                          className="text-green-600 hover:text-green-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-green-50"
                        >
                          <Send className="w-4 h-4" />
                          Send
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        </div>
        </div>
        
        {/* Pagination - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              Showing <span className="font-bold text-blue-600">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of <span className="font-bold text-blue-600">{pagination.totalItems}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fetchPayments(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                Previous
              </button>
              
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              
              <button
                onClick={() => fetchPayments(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create Payment</h2>
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                {/* User Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search user..."
                    />
                    
                    {/* User Dropdown */}
                    {showUserDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map(user => (
                            <div
                              key={user.id}
                              onClick={() => {
                                setPaymentData(prev => ({ ...prev, userId: user.id.toString() }));
                                setSearchTerm(`${user.firstName} ${user.lastName}`);
                                setShowUserDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.firstName} {user.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">{user.email}</div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  user.role === 'Rider' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {user.role}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected User */}
                  {selectedUser && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">
                        {selectedUser.firstName} {selectedUser.lastName}
                      </div>
                      <div className="text-xs text-blue-700">{selectedUser.email}</div>
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount (DA)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amount..."
                    required
                  />
                </div>

                {/* Payment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={paymentData.type}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="Session">Session</option>
                    <option value="Subscription">Subscription</option>
                    <option value="RidingCard">Riding Card</option>
                    <option value="Membership">Membership</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Payment Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentForm(false)}
                    className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !paymentData.userId || !paymentData.amount}
                    className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Creating...' : 'Create Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default Payments;